/**
 * Real-time Updates Manager
 *
 * Enterprise features for billion-scale:
 * - WebSocket connection with automatic reconnection
 * - Exponential backoff for reconnection
 * - Heartbeat/ping-pong for connection health
 * - Event subscription management
 * - Fallback to polling when WebSocket unavailable
 * - Connection state management
 * - Message queue for offline resilience
 */

// Connection states
const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed',
};

class RealtimeManager {
  constructor(options = {}) {
    this.wsUrl = options.wsUrl || this._getWebSocketUrl();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = options.maxReconnectAttempts || 10;
    this.baseReconnectDelay = options.baseReconnectDelay || 1000;
    this.maxReconnectDelay = options.maxReconnectDelay || 30000;
    this.heartbeatInterval = options.heartbeatInterval || 30000;

    this.ws = null;
    this.state = ConnectionState.DISCONNECTED;
    this.subscriptions = new Map(); // channel -> Set<callback>
    this.pendingMessages = []; // Queue for offline messages
    this.heartbeatTimer = null;
    this.reconnectTimer = null;
    this.listeners = new Set(); // State change listeners

    // Bind methods
    this._onOpen = this._onOpen.bind(this);
    this._onMessage = this._onMessage.bind(this);
    this._onError = this._onError.bind(this);
    this._onClose = this._onClose.bind(this);
  }

  /**
   * Get WebSocket URL from API base URL
   */
  _getWebSocketUrl() {
    const apiUrl = import.meta.env.VITE_API_URL || 'https://api.z101.io';
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const host = apiUrl.replace(/^https?:\/\//, '');
    return `${wsProtocol}://${host}/ws`;
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    if (this.state === ConnectionState.CONNECTING ||
        this.state === ConnectionState.CONNECTED) {
      return;
    }

    this._setState(ConnectionState.CONNECTING);

    try {
      const token = localStorage.getItem('auth_token');
      const url = token ? `${this.wsUrl}?token=${token}` : this.wsUrl;

      this.ws = new WebSocket(url);
      this.ws.onopen = this._onOpen;
      this.ws.onmessage = this._onMessage;
      this.ws.onerror = this._onError;
      this.ws.onclose = this._onClose;
    } catch (error) {
      console.error('[RealtimeManager] Connection error:', error);
      this._scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this._clearTimers();
    this.reconnectAttempts = 0;

    if (this.ws) {
      this.ws.onclose = null; // Prevent reconnect on intentional disconnect
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this._setState(ConnectionState.DISCONNECTED);
  }

  /**
   * Subscribe to a channel
   */
  subscribe(channel, callback) {
    if (!this.subscriptions.has(channel)) {
      this.subscriptions.set(channel, new Set());
    }

    this.subscriptions.get(channel).add(callback);

    // Send subscription message if connected
    if (this.state === ConnectionState.CONNECTED) {
      this._sendSubscription(channel);
    }

    // Return unsubscribe function
    return () => this.unsubscribe(channel, callback);
  }

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(channel, callback) {
    const callbacks = this.subscriptions.get(channel);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.subscriptions.delete(channel);
        // Send unsubscription message if connected
        if (this.state === ConnectionState.CONNECTED) {
          this._sendUnsubscription(channel);
        }
      }
    }
  }

  /**
   * Send a message (queued if offline)
   */
  send(message) {
    const payload = typeof message === 'string' ? message : JSON.stringify(message);

    if (this.state === ConnectionState.CONNECTED && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(payload);
    } else {
      // Queue for later
      this.pendingMessages.push(payload);
    }
  }

  /**
   * Add state change listener
   */
  onStateChange(listener) {
    this.listeners.add(listener);
    // Immediately notify of current state
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  /**
   * Get current connection state
   */
  getState() {
    return this.state;
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.state === ConnectionState.CONNECTED;
  }

  // Private methods

  _setState(newState) {
    if (this.state !== newState) {
      this.state = newState;
      this.listeners.forEach(listener => {
        try {
          listener(newState);
        } catch (error) {
          console.error('[RealtimeManager] State listener error:', error);
        }
      });
    }
  }

  _onOpen() {
    console.log('[RealtimeManager] Connected');
    this._setState(ConnectionState.CONNECTED);
    this.reconnectAttempts = 0;

    // Resubscribe to all channels
    this.subscriptions.forEach((_, channel) => {
      this._sendSubscription(channel);
    });

    // Send pending messages
    while (this.pendingMessages.length > 0) {
      const message = this.pendingMessages.shift();
      this.ws.send(message);
    }

    // Start heartbeat
    this._startHeartbeat();
  }

  _onMessage(event) {
    try {
      const data = JSON.parse(event.data);

      // Handle pong response
      if (data.type === 'pong') {
        return;
      }

      // Handle channel messages
      if (data.channel && this.subscriptions.has(data.channel)) {
        this.subscriptions.get(data.channel).forEach(callback => {
          try {
            callback(data.payload || data.data || data);
          } catch (error) {
            console.error('[RealtimeManager] Callback error:', error);
          }
        });
      }

      // Handle broadcast messages (no specific channel)
      if (data.type === 'broadcast' && this.subscriptions.has('*')) {
        this.subscriptions.get('*').forEach(callback => {
          try {
            callback(data.payload || data.data || data);
          } catch (error) {
            console.error('[RealtimeManager] Broadcast callback error:', error);
          }
        });
      }
    } catch (error) {
      console.error('[RealtimeManager] Message parse error:', error);
    }
  }

  _onError(error) {
    console.error('[RealtimeManager] WebSocket error:', error);
  }

  _onClose(event) {
    console.log('[RealtimeManager] Disconnected:', event.code, event.reason);
    this._clearTimers();

    // Don't reconnect on clean close (1000) or auth failure (4001)
    if (event.code === 1000 || event.code === 4001) {
      this._setState(ConnectionState.DISCONNECTED);
      return;
    }

    this._scheduleReconnect();
  }

  _scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[RealtimeManager] Max reconnect attempts reached');
      this._setState(ConnectionState.FAILED);
      return;
    }

    this._setState(ConnectionState.RECONNECTING);

    // Exponential backoff with jitter
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts) +
      Math.random() * 1000,
      this.maxReconnectDelay
    );

    console.log(`[RealtimeManager] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  _startHeartbeat() {
    this._clearTimers();
    this.heartbeatTimer = setInterval(() => {
      if (this.state === ConnectionState.CONNECTED && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      }
    }, this.heartbeatInterval);
  }

  _clearTimers() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  _sendSubscription(channel) {
    this.send({
      type: 'subscribe',
      channel,
      timestamp: Date.now(),
    });
  }

  _sendUnsubscription(channel) {
    this.send({
      type: 'unsubscribe',
      channel,
      timestamp: Date.now(),
    });
  }
}

// Singleton instance
export const realtimeManager = new RealtimeManager();

// Export connection states
export { ConnectionState };

export default RealtimeManager;
